import React, { useEffect, useState } from 'react';
import { X, Clock } from 'lucide-react';
import toast from 'react-hot-toast';
import studyBuilderService from '../../services/studyBuilderService';

/**
 * FECOA-1238: Version history for a live schedule — the original protocol plus
 * every mid-study amendment, newest first. Read-only audit surface.
 *
 * @param {boolean} open
 * @param {() => void} onClose
 * @param {string} scheduleId - the live Schedule UUID
 */
const ScheduleVersionHistoryPanel = ({ open, onClose, scheduleId }) => {
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !scheduleId) return;
    let cancelled = false;
    setLoading(true);
    studyBuilderService
      .getScheduleVersions(scheduleId)
      .then((data) => {
        if (!cancelled) setVersions(Array.isArray(data) ? data : (data?.results || []));
      })
      .catch(() => {
        if (!cancelled) toast.error('Failed to load schedule version history.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, scheduleId]);

  if (!open) return null;

  const fmt = (iso) => {
    if (!iso) return '—';
    try {
      return new Date(iso).toLocaleString();
    } catch {
      return iso;
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/40">
      <div className="bg-white dark:bg-slate-900 w-full max-w-md h-full p-6 overflow-y-auto shadow-xl">
        <div className="flex items-start justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            Schedule version history
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-900 dark:hover:text-gray-200"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {loading && (
          <p className="text-sm text-gray-500">Loading…</p>
        )}

        {!loading && versions.length === 0 && (
          <p className="text-sm text-gray-500">No version history available.</p>
        )}

        <ol className="space-y-4">
          {versions.map((v) => (
            <li
              key={v.id}
              className="border border-gray-200 dark:border-slate-700 rounded-md p-4"
            >
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                  Version {v.version_number}
                  {v.version_number === 1 && (
                    <span className="ml-2 text-xs font-normal text-gray-500">
                      (original protocol)
                    </span>
                  )}
                </span>
                <span className="flex items-center gap-1 text-xs text-gray-500">
                  <Clock className="w-3.5 h-3.5" />
                  {fmt(v.effective_from)}
                </span>
              </div>
              {v.reason && (
                <p className="text-sm text-gray-700 dark:text-gray-300 mb-1">{v.reason}</p>
              )}
              <p className="text-xs text-gray-500">
                {v.created_by ? `By ${v.created_by}` : 'System'} · created {fmt(v.created_at)}
              </p>
            </li>
          ))}
        </ol>
      </div>
    </div>
  );
};

export default ScheduleVersionHistoryPanel;
